import type { FC, ReactNode } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import type { VirtualElement } from '@floating-ui/dom';

export interface ViewportTooltipCoordinate {
    x: number;
    y: number;
}

export interface ViewportTooltipPositionerProps {
    anchorElement: HTMLElement;
    children: ReactNode;
    coordinate: ViewportTooltipCoordinate;
    zIndex?: number;
}

interface ViewportTooltipPosition {
    left: number;
    top: number;
}

/**
 * Positions chart and canvas tooltips against a virtual cursor anchor in the
 * viewport, allowing them to escape clipped visualization containers.
 */
const ViewportTooltipPositioner: FC<ViewportTooltipPositionerProps> = ({
    anchorElement,
    children,
    coordinate,
    zIndex = 1051,
}) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<ViewportTooltipPosition>();
    const reference = useMemo<VirtualElement>(
        () => ({
            contextElement: anchorElement,
            getBoundingClientRect: () => {
                const anchorRect = anchorElement.getBoundingClientRect();
                const left = anchorRect.left + coordinate.x;
                const top = anchorRect.top + coordinate.y;

                return {
                    bottom: top,
                    height: 0,
                    left,
                    right: left,
                    top,
                    width: 0,
                    x: left,
                    y: top,
                    toJSON: () => ({}),
                };
            },
        }),
        [anchorElement, coordinate.x, coordinate.y],
    );

    useLayoutEffect(() => {
        const tooltipElement = tooltipRef.current;

        if (!tooltipElement) {
            return;
        }

        let mounted = true;
        const updatePosition = () => {
            void computePosition(reference, tooltipElement, {
                middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
                placement: 'bottom-start',
                strategy: 'fixed',
            }).then(({ x, y }) => {
                if (mounted) {
                    setPosition({ left: x, top: y });
                }
            });
        };
        const cleanup = autoUpdate(reference, tooltipElement, updatePosition);

        return () => {
            mounted = false;
            cleanup();
        };
    }, [reference]);

    return (
        <div
            data-viewport-tooltip-positioner='true'
            ref={tooltipRef}
            style={{
                left: position?.left ?? 0,
                maxWidth: 'calc(100vw - 1rem)',
                pointerEvents: 'none',
                position: 'fixed',
                top: position?.top ?? 0,
                visibility: position ? 'visible' : 'hidden',
                width: 'max-content',
                zIndex,
            }}
        >
            {children}
        </div>
    );
};

export default ViewportTooltipPositioner;
